// Personal welcome screen - visit bookkeeping on the client (2026-10-16).
//
// A "new entry" is a new browser session (a new tab/app launch) or signing in. Within the same
// session the visit is kept in sessionStorage, so a refresh, switching screens or coming back
// from the preview shows the same greeting and colours and sends no request. The next greeting
// and theme for a new entry come from the server (POST /api/welcome/next), which keeps the
// customer's rotation across devices. If storage or the network fails, the screen still shows a
// valid greeting and theme and never blocks the chat.

import { GREETING_COUNT } from './greetings';
import { THEME_COUNT } from './themes';

const VISIT_KEY = 'cv.welcome.visit';
const SESSION_KEY = 'cv.chat.session';
// The visit the server reserved for the NEXT entry on this device (kept across sessions).
// Showing it needs no network wait; it is then confirmed in the background (2026-10-17: the
// greeting appeared ~5 s late while the client waited for /api/auth/me and the rotation write).
const NEXT_KEY = 'cv.welcome.next';

function safeLocal(storage) {
  const store = storage === undefined ? (typeof window !== 'undefined' ? window.localStorage : null) : storage;
  return safeSession(store);
}

function readReserved(tenantId, localStore) {
  try {
    const parsed = JSON.parse(safeLocal(localStore).get(NEXT_KEY) || 'null');
    return validVisit(parsed, tenantId) ? parsed : null;
  } catch {
    return null;
  }
}

function storeReserved(tenantId, next, localStore) {
  const candidate = next ? { tenantId, visitId: next.visitId, greeting: next.greeting, theme: next.theme } : null;
  if (candidate && validVisit(candidate, tenantId)) safeLocal(localStore).set(NEXT_KEY, JSON.stringify(candidate));
}

export function safeSession(storage) {
  const store = storage === undefined ? (typeof window !== 'undefined' ? window.sessionStorage : null) : storage;
  return {
    get(key) { try { return store ? store.getItem(key) : null; } catch { return null; } },
    set(key, value) { try { if (store) store.setItem(key, value); return true; } catch { return false; } },
    remove(key) { try { if (store) store.removeItem(key); } catch { /* storage unavailable */ } }
  };
}

export function newVisitId() {
  const random = (typeof crypto !== 'undefined' && crypto.getRandomValues)
    ? Array.from(crypto.getRandomValues(new Uint8Array(9)), byte => byte.toString(36)).join('')
    : Math.random().toString(36).slice(2, 14);
  return `v_${Date.now().toString(36)}_${random}`.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 60);
}

function validVisit(value, tenantId) {
  return value && value.tenantId === tenantId && typeof value.visitId === 'string'
    && Number.isInteger(value.greeting) && value.greeting >= 0 && value.greeting < GREETING_COUNT
    && Number.isInteger(value.theme) && value.theme >= 0 && value.theme < THEME_COUNT;
}

export function readVisit(tenantId, storage) {
  try {
    const parsed = JSON.parse(safeSession(storage).get(VISIT_KEY) || 'null');
    return validVisit(parsed, tenantId) ? parsed : null;
  } catch {
    return null;
  }
}

/** Forgets the current visit, so the next screen load counts as a new entry (sign in / sign out). */
export function startNewEntry(storage) {
  const session = safeSession(storage);
  session.remove(VISIT_KEY);
  session.remove(SESSION_KEY);
}

/**
 * Returns this session's visit, asking the server for the next greeting/theme only on a new entry.
 * `fetchNext(visitId)` resolves to { greeting, theme }. `random(n)` is used only for the offline
 * fallback.
 */
export async function ensureVisit({ tenantId, fetchNext, storage, localStore, random = n => Math.floor(Math.random() * n) }) {
  const existing = readVisit(tenantId, storage);
  if (existing) return { ...existing, source: 'session' };
  const session = safeSession(storage);

  // Instant path: the greeting/theme reserved for this entry on the previous one.
  const reserved = readReserved(tenantId, localStore);
  if (reserved) {
    session.set(VISIT_KEY, JSON.stringify(reserved));
    safeLocal(localStore).remove(NEXT_KEY);
    // Confirm it and reserve the following one, in the background (never replaces what is shown).
    Promise.resolve()
      .then(() => fetchNext(reserved.visitId))
      .then(answer => storeReserved(tenantId, answer?.next, localStore))
      .catch(() => {});
    return { ...reserved, source: 'reserved' };
  }

  // Re-use a visit id that was already sent but whose answer was not stored (e.g. the page was
  // refreshed mid-request): the server answers the same visit with the same result.
  let pending = null;
  try { pending = JSON.parse(session.get(`${VISIT_KEY}.pending`) || 'null'); } catch { pending = null; }
  const visitId = pending?.tenantId === tenantId && typeof pending.visitId === 'string' ? pending.visitId : newVisitId();
  session.set(`${VISIT_KEY}.pending`, JSON.stringify({ tenantId, visitId }));
  let visit;
  try {
    const answer = await fetchNext(visitId);
    visit = { tenantId, visitId, greeting: answer.greeting, theme: answer.theme };
    if (!validVisit(visit, tenantId)) throw new Error('invalid welcome answer');
    visit.source = 'server';
    storeReserved(tenantId, answer.next, localStore);
  } catch {
    visit = { tenantId, visitId, greeting: random(GREETING_COUNT), theme: random(THEME_COUNT), source: 'fallback' };
  }
  session.set(VISIT_KEY, JSON.stringify({ tenantId, visitId, greeting: visit.greeting, theme: visit.theme }));
  session.remove(`${VISIT_KEY}.pending`);
  return visit;
}

/**
 * True exactly once per session: the chat should open as a fresh welcome screen instead of the
 * last conversation. Later loads in the same session (refresh, screen switch) return false so an
 * opened conversation is never replaced. If sessionStorage is unavailable the previous behaviour
 * (continue the last conversation) is kept.
 */
export function claimFreshChatSession(storage) {
  const session = safeSession(storage);
  if (session.get(SESSION_KEY)) return false;
  return session.set(SESSION_KEY, String(Date.now()));
}

const NAME_KEYS = ['firstName', 'givenName', 'name', 'displayName', 'fullName'];
const GREETING_WORDS = /^(שלום|היי|הי|hello|hi|hey)[,!\s]*/i;

/** The customer's first name from verified profile data, or null (never the e-mail address). */
export function firstNameFrom(tenant) {
  for (const key of NAME_KEYS) {
    const raw = typeof tenant?.[key] === 'string' ? tenant[key] : '';
    const cleaned = raw.replace(GREETING_WORDS, '').trim();
    if (!cleaned || cleaned.includes('@')) continue;
    const first = cleaned.split(/\s+/)[0].replace(/[.,!?;:]+$/, '');
    if (/^[\p{L}][\p{L}'׳’-]{0,29}$/u.test(first) && !/\d/.test(first)) return first;
  }
  return null;
}

/** "שלום, אברהם 👋" / "Hi, Avraham 👋", or a general greeting when there is no valid name. */
export function greetingLine(name, locale = 'he') {
  if (locale === 'en') return name ? `Hi, ${name} 👋` : 'Hi there 👋';
  return name ? `שלום, ${name} 👋` : 'שלום 👋';
}
